/*
 * An edition holds its licenses as `{ licenseId, tierId }` pairs: the names live in the license
 * catalog (`GET /licensing/addons`), so every label here is a lookup into it. The fallbacks are
 * deliberate words rather than blanks: a blank cell would read as missing data, when what is
 * missing is the catalog row.
 */
export const UNKNOWN_LICENSE = 'Unknown license';
export const UNKNOWN_TIER = 'Unknown tier';

export const licenseById = (catalog, licenseId) => {
	return (catalog ?? []).find((license) => { return license.id === licenseId; }) ?? null;
};

export const tierById = (license, tierId) => {
	return (license?.addonTiers ?? []).find((tier) => { return tier.id === tierId; }) ?? null;
};

/*
 * The same label the product form gives a tier, so a tier reads the same on both screens.
 */
export const tierLabel = (tier) => {
	if (!tier) {
		return UNKNOWN_TIER;
	}

	const unitInfo = tier.unitOfMeasurement?.symbol ? ` (${tier.unitOfMeasurement.symbol})` : '';

	return `${tier.name ?? tier.code ?? UNKNOWN_TIER}${unitInfo}`;
};

export const licenseLabel = (license) => {
	return license?.name ?? UNKNOWN_LICENSE;
};

export const tierOptions = (license) => {
	return (license?.addonTiers ?? []).map((tier) => {
		return { value: tier.id, label: tierLabel(tier) };
	});
};

/*
 * Resolves an edition's members against the catalog once, so the table and the modal render the
 * same names from the same lookup.
 */
export const describeEditionLicenses = (edition, catalog) => {
	return (edition?.licenses ?? []).map((member) => {
		const license = licenseById(catalog, member.licenseId);
		const tier = tierById(license, member.tierId);

		return {
			licenseId: member.licenseId,
			tierId: member.tierId,
			licenseName: licenseLabel(license),
			licenseKnown: Boolean(license),
			tierName: tierLabel(tier),
			tierOptions: tierOptions(license)
		};
	});
};

/*
 * The PATCH replaces the whole license list, so a save with nothing changed is a no-op the server
 * still runs as a job. Order matters: the server keeps it, and a reordered list is a change.
 */
export const haveLicensesChanged = (original, edited) => {
	const before = original ?? [];
	const after = edited ?? [];

	if (before.length !== after.length) {
		return true;
	}

	return before.some((member, index) => {
		return member.licenseId !== after[index]?.licenseId || member.tierId !== after[index]?.tierId;
	});
};

export const toLicensesPayload = (members) => {
	return (members ?? []).map((member) => {
		return { licenseId: member.licenseId, tierId: member.tierId };
	});
};
